import { RISK_RULES } from "../../../contracts/src/risk.ts";
import {
  strategyDisplayName,
  strategyNames,
} from "../../../contracts/src/index.ts";

export { strategyDisplayName, strategyNames };

export type LinkStatus = "verified" | "linked" | "pending" | "not_configured";

export interface ExternalVerificationConfig {
  url: string;
  status: LinkStatus;
}

export interface SiteConfig {
  brandName: string;
  eaName: string;
  tagline: string;
  version: string;
  dataVersion: string;
  updatedAt: string;
  canonicalUrl: string;
  workerUrl: string;
  publicAccountId: string;
  currency: "TWD";
  price: number;
  priceNote: string;
  myfxbook: ExternalVerificationConfig;
  signalStart: ExternalVerificationConfig;
  lastVerifiedAt: string;
  instagramUrl: string;
  lineUrl: string;
  telegramUrl: string;
  contactEmail: string;
  supportedProducts: string[];
  suggestedCapital: string;
  licenseText: string;
  updatePolicy: string;
  supportText: string;
  schemaAvailability?: string;
  webAnalyticsToken: string;
  riskRules: typeof RISK_RULES;
  riskDisclosure: string;
}

export const siteConfig: SiteConfig = Object.freeze<SiteConfig>({
  brandName: "Hybrid Quant",
  eaName: "Hybrid Quant EA",
  tagline: "Rules first. Risk defined. Performance visible.",
  version: "5.5.0",
  dataVersion: "v5.4",
  updatedAt: "2026-08-07",
  canonicalUrl: "https://ea-performance-dashboard.pages.dev",
  workerUrl: "https://ea-performance-api.kefai5270.workers.dev",
  publicAccountId: "main-account",
  currency: "TWD",
  price: 6600,
  priceNote: "售價與授權內容以聯絡確認為準。",
  myfxbook: { url: "https://www.myfxbook.com/portfolio/million-profit-robot/12038054", status: "linked" },
  signalStart: { url: "https://www.signalstart.com/analysis/amazing-system-7275/289129", status: "linked" },
  lastVerifiedAt: "",
  instagramUrl: "",
  lineUrl: "",
  telegramUrl: "",
  contactEmail: "",
  supportedProducts: ["依授權版本與實際測試條件確認"],
  suggestedCapital: "依帳戶槓桿、交易商品與單筆風險設定評估，不提供固定最低資金承諾。",
  licenseText: "授權帳戶數、有效期間與移機方式，以聯絡確認內容為準。",
  updatePolicy: "版本修正與功能更新政策，以購買時提供的版本說明為準。",
  supportText: "提供安裝文件、基礎參數說明與約定範圍內的設定協助。",
  webAnalyticsToken: "",
  riskRules: RISK_RULES,
  riskDisclosure:
    "外匯、差價合約及其他槓桿商品具有高度風險，可能造成部分或全部本金損失。歷史與即時績效均不代表未來結果；本站僅供系統運作與績效資訊查閱，不構成投資建議、招攬或任何獲利保證。",
});

export const productCopy = Object.freeze({
  short:
    "Hybrid Quant EA 是以規則型交易為核心的多元素混合策略，整合趨勢結構、突破確認、動能過濾、波動調節與多層風控。系統依條件執行，不承諾預測每次行情，並以公開資料持續呈現實際運作結果。",
  full:
    "Hybrid Quant EA 將趨勢結構、關鍵區間突破、動能品質、波動率與多時間框架資訊整合為單一決策流程。系統不依賴臨場情緒，也不以某一個訊號決定所有交易；只有在多項條件達到一致時，交易才被允許。進場後由初始風險、保本移動、追蹤止盈、條件失效退出與組合曝險限制共同管理。規則型引擎仍是交易核心，AI 與大數據分析層用於市場狀態分類、績效監控、風險評分、參數穩健性檢查及策略權重輔助，不宣稱能準確預測價格，也不宣稱具備尚未實作的即時自我訓練能力。網站以成交紀錄重建 Historical Balance，並在 Publisher 啟用後呈現實際 Live Balance 與 Live Equity，讓資料來源、回撤與限制保持可辨識。",
});

export const strategyModules = Object.freeze([
  ["01", "趨勢結構", "辨識主要市場方向與結構，降低在無方向區間追價的機會。"],
  ["02", "突破確認", "監控關鍵價格區間，要求有效突破與條件確認後才允許執行。"],
  ["03", "動能過濾", "檢查行情推進品質，降低動能不足與假突破造成的干擾。"],
  ["04", "波動自適應", "依市場波動調整進場門檻、風險距離與部位，不以固定參數套用所有情境。"],
  ["05", "多時間框架", "以較高時間框架定義背景，再由執行週期尋找符合規則的機會。"],
  ["06", "動態出場", "結合初始風險、保本、追蹤止盈與條件失效退出。"],
  ["07", "組合風控", "限制單筆、相關商品、同方向曝險、持倉數量、交易時段與冷卻時間。"],
  ["08", "分析輔助層", "用於市場分類、績效監控、風險評分、穩健性分析與策略權重輔助。"],
]);

export const deliberateExclusions = Object.freeze([
  "不使用無上限馬丁或無限制攤平",
  "不以極端槓桿追求短期漂亮曲線",
  "不隱藏已發生的回撤或離線狀態",
  "不將入金、出金或 Credit 誤算為策略報酬",
  "不以虛構回測、評論或購買通知冒充實際紀錄",
  "不承諾固定月報酬、勝率或回本時間",
]);

export function validHttpsUrl(value: string): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

export function validEmail(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 254) return null;
  return /^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?(?:\.[A-Z0-9](?:[A-Z0-9-]{0,61}[A-Z0-9])?)+$/i.test(trimmed)
    ? trimmed
    : null;
}

export function verificationDisplay(
  verification: ExternalVerificationConfig,
): { label: string; canOpen: boolean } {
  const canOpen = Boolean(validHttpsUrl(verification.url));
  const labels: Record<LinkStatus, string> = {
    verified: "已由人工確認驗證狀態",
    linked: "已連結公開頁面",
    pending: "等待確認",
    not_configured: "尚未設定",
  };
  const effectiveStatus: LinkStatus = canOpen ? verification.status : "not_configured";
  return { label: labels[effectiveStatus], canOpen };
}
